import React from 'react'
import { FiDollarSign, FiClock, FiCheckCircle, FiAlertCircle, FiExternalLink } from 'react-icons/fi'
import { formatTimeAgo } from '@/utils/formatters'
import { CurrencyDisplay } from '@/components/currency'

interface EscrowStatusProps {
    escrowId: string
    escrowStatus: string
    amountHeld: number
    currency: string
    releasedAt?: string
    autoApprovalDeadline?: string
    className?: string
}

const EscrowStatus: React.FC<EscrowStatusProps> = ({
    escrowId,
    escrowStatus,
    amountHeld,
    currency,
    releasedAt,
    autoApprovalDeadline,
    className = ''
}) => {
    const getStatusColor = (status: string) => {
        switch (status) {
            case 'pending':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300'
            case 'funded':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
            case 'submitted':
                return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300'
            case 'approved':
                return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
            case 'released':
                return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
            case 'disputed':
                return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
            case 'cancelled':
                return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
        }
    }

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'pending':
                return <FiClock className="w-4 h-4 text-yellow-500" />
            case 'funded':
                return <FiDollarSign className="w-4 h-4 text-blue-500" />
            case 'submitted':
                return <FiExternalLink className="w-4 h-4 text-purple-500" />
            case 'approved':
                return <FiCheckCircle className="w-4 h-4 text-green-500" />
            case 'released':
                return <FiCheckCircle className="w-4 h-4 text-green-500" />
            case 'disputed':
                return <FiAlertCircle className="w-4 h-4 text-red-500" />
            case 'cancelled':
                return <FiAlertCircle className="w-4 h-4 text-gray-500" />
            default:
                return <FiAlertCircle className="w-4 h-4 text-gray-500" />
        }
    }

    const getStatusText = (status: string) => {
        switch (status) {
            case 'pending':
                return 'Payment pending'
            case 'funded':
                return 'Payment secured in escrow'
            case 'submitted':
                return 'Work submitted, awaiting review'
            case 'approved':
                return 'Work approved, releasing payment'
            case 'released':
                return 'Payment released to worker'
            case 'disputed':
                return 'Payment disputed'
            case 'cancelled':
                return 'Escrow cancelled'
            default:
                return 'Unknown status'
        }
    }

    return (
        <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 border border-gray-200 dark:border-gray-700 ${className}`}>
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Escrow Status
                    </h3>
                    <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full">
                        {escrowId}
                    </span>
                </div>
                {getStatusIcon(escrowStatus)}
            </div>

            <div className="space-y-3">
                <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Status:</span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(escrowStatus)}`}>
                        {getStatusText(escrowStatus)}
                    </span>
                </div>

                <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Amount:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                        <CurrencyDisplay
                            amount={amountHeld}
                            currency={currency}
                            compact
                        />
                    </span>
                </div>

                {autoApprovalDeadline && (
                    <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Auto-approval:</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {new Date(autoApprovalDeadline).toLocaleDateString()}
                        </span>
                    </div>
                )}

                {releasedAt && (
                    <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Released:</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {formatTimeAgo(releasedAt)}
                        </span>
                    </div>
                )}
            </div>
        </div>
    )
}

export default EscrowStatus